/**
 * Allowlist sanitizer for the small amount of HTML the translation files carry.
 *
 * TutorialsModal renders seven translation strings through dangerouslySetInnerHTML.
 * Those strings are static build assets today, so nothing is exposed right now — but
 * it makes the locale files an executable code path, and locale files are exactly the
 * kind of asset that later becomes editable (a translator UI, a CMS, a contributed PR).
 *
 * Parsing happens in a detached document via DOMParser: it does not execute scripts,
 * does not fetch anything, and never touches the live DOM. Everything outside the
 * allowlist below is unwrapped (its text survives, its markup does not).
 */
const ALLOWED_TAGS = new Set([
  "B", "STRONG", "I", "EM", "U", "BR", "P", "SPAN", "UL", "OL", "LI", "SMALL",
]);

/** Only inline style survives, and only on the layout elements that use it. */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  UL: new Set(["style"]),
  OL: new Set(["style"]),
  LI: new Set(["style"]),
  P: new Set(["style"]),
  SPAN: new Set(["style"]),
};

/** url(...) and expression(...) are the ways a style attribute can still fetch or run. */
const UNSAFE_STYLE = /(url\s*\(|expression\s*\(|javascript\s*:|@import)/i;

const clean = (node: Element): void => {
  // Snapshot: unwrapping mutates the live child list.
  Array.from(node.children).forEach(clean);

  Array.from(node.attributes).forEach((attr) => {
    const allowed = ALLOWED_ATTRS[node.tagName] ?? new Set<string>();
    const name = attr.name.toLowerCase();

    if (!allowed.has(name)) {
      node.removeAttribute(attr.name);
      return;
    }

    // An allowlisted style attribute can still fetch or execute through url(),
    // expression() or @import, so its value is checked as well as its name.
    if (name === "style" && UNSAFE_STYLE.test(attr.value)) {
      node.removeAttribute(attr.name);
    }
  });

  if (!ALLOWED_TAGS.has(node.tagName)) {
    // Keep the text, drop the element.
    node.replaceWith(...Array.from(node.childNodes));
  }
};

export const sanitizeRichText = (html: string): string => {
  if (!html) {
    return "";
  }

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    Array.from(doc.body.children).forEach(clean);

    return doc.body.innerHTML;
  } catch {
    // If parsing is unavailable, fall back to rendering nothing rather than
    // rendering unsanitised markup.
    return "";
  }
};
